// Forum Admin API module
import { BaseApiClient } from './BaseApiClient';
import type { ApiResponse } from '@/types/api';

// Forum types
export interface ForumPost {
  id: string;
  title: string;
  content: string;
  category: string;
  language: string;
  author_id: string;
  author_name: string;
  author_picture?: string;
  created_at: string;
  updated_at: string;
  view_count: number;
  reply_count: number;
  like_count: number;
  is_pinned: boolean;
  is_locked: boolean;
  is_solved: boolean;
  is_hidden?: boolean;
  hidden_by?: string;
  hidden_at?: string;
  hidden_reason?: string;
}

export interface ForumReply {
  id: string;
  post_id: string;
  content: string;
  author_id: string;
  author_name: string;
  author_picture?: string;
  created_at: string;
  updated_at: string;
  like_count: number;
  is_solution: boolean;
  is_hidden?: boolean;
  hidden_by?: string;
  hidden_at?: string;
  hidden_reason?: string;
}

export interface ReplyWithPostContext {
  reply: ForumReply;
  post: ForumPost;
}

export interface ForumActivityResponse {
  success: boolean;
  data: {
    posts: ForumPost[];
    replies: ReplyWithPostContext[];
  };
  filters: {
    languages: string[];
    category?: string;
    search?: string;
  };
}

export interface ForumPostListResponse {
  posts: ForumPost[];
  total: number;
  page: number;
  limit: number;
  has_next: boolean;
}

export interface ForumStatsResponse {
  total_posts: number;
  pinned_posts: number;
  solved_posts: number;
  hidden_posts: number;
  posts_by_category: Record<string, number>;
  generated_at: string;
}

export interface TranslatePostRequest {
  target_languages: string[];
  translate_replies?: boolean;
  preview_only?: boolean;
  confirmed_translations?: Array<{
    language: string;
    title: string;
    content: string;
  }>;
}

export interface TranslatePostResponse {
  success: boolean;
  message: string;
  original_post_id: string;
  original_language: string;
  translations: Array<{
    post_id?: string;
    language: string;
    title: string;
    content?: string;
  }>;
}

export class ForumApi extends BaseApiClient {
  constructor() {
    super('ForumAPI');
  }

  /**
   * Get forum activity (latest posts + replies) across languages
   */
  async getForumActivity(params?: {
    languages?: string;
    category?: string;
    search?: string;
    limit_posts?: number;
    limit_replies?: number;
    include_hidden_posts?: boolean;
  }): Promise<ForumActivityResponse> {
    const response = await this.api.get<ForumActivityResponse>('/forum/activity', { params });
    return response.data;
  }

  /**
   * List forum posts for moderation
   */
  async getForumPosts(params?: {
    languages?: string;
    category?: string;
    page?: number;
    limit?: number;
    show_pinned_only?: boolean;
    show_solved_only?: boolean;
    include_hidden?: boolean;
    search?: string;
  }): Promise<ForumPostListResponse> {
    const response = await this.api.get<ForumPostListResponse>('/forum/posts', { params });
    return response.data;
  }

  /**
   * Get forum moderation statistics
   */
  async getForumStats(): Promise<ForumStatsResponse> {
    const response = await this.api.get<ForumStatsResponse>('/forum/stats');
    return response.data;
  }

  /**
   * Pin or unpin a forum post
   */
  async pinPost(postId: string): Promise<ApiResponse<{ is_pinned: boolean; post_id: string }>> {
    const response = await this.api.post<ApiResponse<{ is_pinned: boolean; post_id: string }>>(
      `/forum/posts/${postId}/pin`
    );
    return response.data;
  }

  /**
   * Mark or unmark a forum post as solved
   */
  async markPostSolved(postId: string): Promise<ApiResponse<{ is_solved: boolean; post_id: string }>> {
    const response = await this.api.post<ApiResponse<{ is_solved: boolean; post_id: string }>>(
      `/forum/posts/${postId}/solve`
    );
    return response.data;
  }

  /**
   * Hide a forum post
   */
  async hidePost(postId: string, reason?: string): Promise<ApiResponse<void>> {
    const response = await this.api.post<ApiResponse<void>>(`/forum/posts/${postId}/hide`, { reason });
    return response.data;
  }

  /**
   * Unhide a forum post
   */
  async unhidePost(postId: string): Promise<ApiResponse<void>> {
    const response = await this.api.post<ApiResponse<void>>(`/forum/posts/${postId}/unhide`);
    return response.data;
  }

  /**
   * Delete a forum post (admin)
   */
  async deletePost(postId: string): Promise<ApiResponse<{ action: string; post_id: string; reply_count?: number }>> {
    const response = await this.api.delete<ApiResponse<{ action: string; post_id: string; reply_count?: number }>>(
      `/forum/posts/${postId}`
    );
    return response.data;
  }

  /**
   * Translate a forum post to other languages
   */
  async translatePost(postId: string, request: TranslatePostRequest): Promise<TranslatePostResponse> {
    const response = await this.api.post<TranslatePostResponse>(`/forum/posts/${postId}/translate`, request);
    return response.data;
  }

  /**
   * Tombstone a reply (replace content with moderation message)
   */
  async tombstoneReply(replyId: string, reason?: string): Promise<ApiResponse<{ action: string; reply_id: string }>> {
    const response = await this.api.post<ApiResponse<{ action: string; reply_id: string }>>(
      `/forum/replies/${replyId}/tombstone`,
      { reason }
    );
    return response.data;
  }

  /**
   * Delete a reply (admin hard delete)
   */
  async deleteReply(replyId: string): Promise<ApiResponse<{ action: string; reply_id: string }>> {
    const response = await this.api.delete<ApiResponse<{ action: string; reply_id: string }>>(
      `/forum/replies/${replyId}`
    );
    return response.data;
  }
}


